import * as React from 'react';

function updateIframeSrc(src, isActive) {
  try {
    const url = new URL(src);
    const host = url.hostname.toLowerCase();

    if (host.includes("youtube.com") || host.includes("youtu.be")) {
      url.searchParams.set("autoplay", isActive ? "1" : "0");
      url.searchParams.set("mute", "1");
      url.searchParams.set("playsinline", "1");
      return url.toString();
    }

    if (host.includes("instagram.com") || host.includes("espn.com")) {
      if (isActive) {
        url.searchParams.set("autoplay", "1");
      } else {
        url.searchParams.delete("autoplay");
      }
      return url.toString();
    }

    return src;
  } catch {
    return src;
  }
}

export function CarouselItem ({
  children,
  width,
  height,
  isActive = false,
  isPlaybackEnabled = true,
  hasUserInteracted = false,
  onPlaybackEnd,
  playbackMode = "timer",
  previewYouTube = false
}){
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    if (!isActive) {
      video.pause();
      video.currentTime = 0;
      return undefined;
    }

    if (!isPlaybackEnabled) {
      video.pause();
      return undefined;
    }

    video.muted = !hasUserInteracted;
    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    return undefined;
  }, [hasUserInteracted, isActive, isPlaybackEnabled]);

  const enhanceChild = React.useCallback((node) => {
    if (!React.isValidElement(node)) {
      return node;
    }

    if (node.type === "video") {
      const handleEnded = (event) => {
        node.props.onEnded?.(event);
        onPlaybackEnd?.({
          hasSound: !event.currentTarget.muted && event.currentTarget.volume > 0
        });
      };

      return React.cloneElement(node, {
        autoPlay: false,
        onEnded: handleEnded,
        playsInline: node.props.playsInline ?? true,
        ref: videoRef
      });
    }

    if (typeof node.type !== "string") {
      return React.cloneElement(node, {
        hasUserInteracted,
        isActive,
        isPlaybackEnabled,
        onPlaybackEnd,
        previewOnly: previewYouTube
      });
    }

    if (node.type === "iframe" && typeof node.props.src === "string") {
      return React.cloneElement(node, {
        src: updateIframeSrc(node.props.src, isActive)
      });
    }

    if (!node.props.children) {
      return node;
    }

    return React.cloneElement(node, {
      children: React.Children.map(node.props.children, enhanceChild)
    });
  }, [hasUserInteracted, isActive, isPlaybackEnabled, onPlaybackEnd, previewYouTube]);

  return(
    <div
      className="carouselItem"
      data-active={isActive}
      data-playback-mode={playbackMode}
      style ={{ width: width, height: height }}
    >
      {React.Children.map(children, enhanceChild)}
    </div>
  )
}
